import { logWarn } from './logger';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND ? `${BACKEND}/api` : '/api';
const SESSION_KEY = 'ctrl-charlie-session';

const genId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

export const getChatSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = genId();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (e) {
    logWarn('charlie: sessionStorage unavailable', e);
    return genId();
  }
};

export const resetChatSession = () => {
  const sid = genId();
  try { sessionStorage.setItem(SESSION_KEY, sid); } catch (e) { logWarn('charlie: reset failed', e); }
  return sid;
};

export const fetchChatHistory = async (sessionId) => {
  const res = await fetch(`${API}/chat/history/${encodeURIComponent(sessionId)}`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.messages || [];
};

export const submitChatLead = async (payload) => {
  const res = await fetch(`${API}/chat/lead`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`lead failed: ${res.status}`);
  return res.json();
};

/** POST a message and consume the SSE stream. Resolves when the stream ends. */
export const streamChatMessage = async ({ sessionId, message, pagePath, onDelta, onDone, onError, signal }) => {
  let res;
  try {
    res = await fetch(`${API}/chat/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, message, page_path: pagePath }),
      signal,
    });
  } catch (e) {
    if (e.name !== 'AbortError') onError('Network error — please check your connection and try again.');
    return;
  }
  if (!res.ok || !res.body) {
    let detail = 'Charlie Echo is unavailable right now. Please try again shortly.';
    try { detail = (await res.json()).detail || detail; } catch (e) { /* keep default */ }
    onError(detail);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finished = false;
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop() || '';
      for (const evt of events) {
        const line = evt.split('\n').find((l) => l.startsWith('data: '));
        if (!line) continue;
        let payload;
        try { payload = JSON.parse(line.slice(6)); } catch (e) { continue; }
        if (payload.type === 'delta') onDelta(payload.text);
        else if (payload.type === 'done') { finished = true; onDone(payload); }
        else if (payload.type === 'error') { finished = true; onError(payload.message); }
      }
    }
  } catch (e) {
    if (e.name === 'AbortError') return;
    logWarn('charlie: stream read failed', e);
  }
  if (!finished) onDone({ sources: [], offer_contact: false });
};
